import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, User, Phone, Building2, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface CMSDetailPageProps {
  title: string;
  description: string;
  image: string;
  updateTime: string;
  views: number;
  category: string;
  categoryPath: string;
  content: string;
  features?: string[];
}

export default function CMSDetailPage({
  title,
  description,
  image,
  updateTime,
  views,
  category,
  categoryPath,
  content,
  features = [],
}: CMSDetailPageProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    email: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
    setFormData({ name: '', phone: '', company: '', email: '', message: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* 统一Header */}
      <Header />

      {/* Main Content */}
      <main className="flex-1 py-8">
        <div className="section-container">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-1 hover:text-[#1c5bff] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              返回
            </button>
            <span>/</span>
            <Link to="/" className="hover:text-[#1c5bff]">首页</Link>
            <span>/</span>
            <Link to={categoryPath} className="hover:text-[#1c5bff]">{category}</Link>
            <span>/</span>
            <span className="text-gray-900 line-clamp-1">{title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Article Content */}
            <div className="lg:col-span-2">
              <article className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {/* Hero Image */}
                <div className="aspect-video overflow-hidden">
                  <img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="p-6 lg:p-8">
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    {title}
                  </h1>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      更新时间：{updateTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {views.toLocaleString()} 次浏览
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    {description}
                  </p>

                  {/* Main Content */}
                  <div className="prose prose-gray max-w-none">
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {content}
                    </div>
                  </div>

                  {/* Features */}
                  {features.length > 0 && (
                    <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">核心优势</h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-[#1c5bff] flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            </div>

            {/* Sidebar - Inquiry Form */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 bg-[#1c5bff]/10 rounded-lg flex items-center justify-center">
                    <Send className="w-5 h-5 text-[#1c5bff]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">在线咨询</h3>
                    <p className="text-sm text-gray-500">留下您的信息，我们尽快联系您</p>
                  </div>
                </div>

                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">提交成功</h4>
                    <p className="text-gray-500">我们会尽快与您联系</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-sm">
                        联系人 <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="请输入联系人姓名"
                          value={formData.name}
                          onChange={handleChange}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-sm">
                        联系电话 <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="请输入联系电话"
                          value={formData.phone}
                          onChange={handleChange}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="company" className="text-sm">
                        公司名称 <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative mt-1">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          placeholder="请输入公司名称"
                          value={formData.company}
                          onChange={handleChange}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-sm">
                        邮箱地址
                      </Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="请输入邮箱地址（选填）"
                          value={formData.email}
                          onChange={handleChange}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm">
                        咨询内容
                      </Label>
                      <textarea
                        id="message"
                        name="message"
                        rows={3}
                        placeholder="请输入您想咨询的内容（选填）"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#1c5bff] hover:bg-[#1547cc] text-white"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      提交咨询
                    </Button>

                    <p className="text-xs text-gray-400 text-center">
                      提交即表示您同意我们的服务条款和隐私政策
                    </p>
                  </form>
                )}

                {/* Contact Info */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-2">或直接联系我们</p>
                  <p className="text-lg font-bold text-[#1c5bff]">028-85002901</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 统一Footer */}
      <Footer />
    </div>
  );
}
